import { Grid, Tabs, Tab, Input as MuiInput, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, FocusEvent, useEffect, useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  FileUpload,
  Select,
  TextField,
  FileWithPreview,
  ConfirmationDialog
} from '../../../../lib/components';
import { GalleryDialog } from '../../../benefits/customBenefitDialogs';
import { useStepper } from '../../partials/actionStepperPartial';
import { useCreateCustomBenefit } from '../hooks/useCreateCustomBenefit';
import { isValidUrl, normalizeUrl } from '../../../../misc/url';
import { ImageIcon, DeleteIcon, ArrowForwardIconSvg } from '../../../../lib/icons';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { InputFieldLabel } from '../../../../lib/components/atomic/input/shared/fieldLabel';
import { getIconOptions } from '../../../../lib/components/compound/dataDisplay/customBenefitIcon';
import { theme } from '../../../../lib/theme';
import { HeaderImageFromGalleryEnum } from '@pxtra-client-lib-legacy/gen';
import { Button } from '../../../../lib/components/atomic/button';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import image1 from '../../../../lib/images/pxt_app_widget_explained.png';
import image2 from '../../../../lib/images/pxt_app_info_custom_benefit_explained.png';

export const tabTheme = createTheme({
  palette: {
    primary: { main: theme.palette.heaven.main }
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '1.1rem'
        }
      }
    }
  }
});

export const descriptionTheme = createTheme({
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          fontSize: '1rem',
          background: theme.palette.grey['50'],
          padding: '16.5px',
          border: '1px solid transparent',
          gap: '0.75rem',
          borderRadius: '16px',
          '&.Mui-focused, &:hover': { border: `1px solid ${theme.palette.grey['100']}` },
          '& > textarea': {
            textOverflow: 'ellipsis',
            textAlign: 'start',
            verticalAlign: 'top'
          },
          '& > textarea::placeholder': { textAlign: 'start', verticalAlign: 'top' },
          fontFamily: 'Rubik, sans-serif'
        }
      }
    }
  }
});

const iconOptions = getIconOptions();

export const BenefitProfile = observer(() => {
  const { newCustomBenefit } = useCreateCustomBenefit();
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { setNextButtonConfig, activeStep, setActiveStep, showInfoDrawer, setShowInfoDrawer } =
    useStepper();
  const [tabValue, setTabValue] = useState(0);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [showDeleteTranslationDialog, setShowDeleteTranslationDialog] = useState(false);
  const [nameError, setNameError] = useState('');
  const [buttonNameError, setButtonNameError] = useState('');
  const [buttonLinkError, setButtonLinkError] = useState('');
  const regularPageExitRef = useRef(false);

  useEffect(() => {
    setNextButtonConfig({
      text: t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        if (!newCustomBenefit.name_de) {
          snackbar.enqueueSnackbar({
            message: t('custom:error_missing_name'),
            variant: 'error'
          });
        } else if (
          !newCustomBenefit.headerImagePreview &&
          !newCustomBenefit.headerImageFromGallery
        ) {
          snackbar.enqueueSnackbar({
            message: t('custom:error_missing_header_image'),
            variant: 'error'
          });
        } else if (!newCustomBenefit.description_de) {
          snackbar.enqueueSnackbar({
            message: t('custom:error_missing_description'),
            variant: 'error'
          });
        } else if (newCustomBenefit.translationAdded && !newCustomBenefit.name_en) {
          snackbar.enqueueSnackbar({
            message: t('custom:error_missing_name_en'),
            variant: 'error'
          });
        } else if (newCustomBenefit.translationAdded && !newCustomBenefit.description_en) {
          snackbar.enqueueSnackbar({
            message: t('custom:error_missing_description_en'),
            variant: 'error'
          });
        } else {
          regularPageExitRef.current = true;
          setActiveStep(activeStep + 1);
        }
      }
    });
    return () => {
      if (!regularPageExitRef.current) newCustomBenefit.clear();
    };
  }, []);

  const handleTranslationButtonClick = () => {
    if (newCustomBenefit.translationAdded) {
      setShowDeleteTranslationDialog(true);
    } else {
      newCustomBenefit.translationAdded = true;
      setTabValue(1);
    }
  };

  const openGallery = () => setShowGalleryDialog(true);

  const handleImageSelect = (imageName: string) => {
    newCustomBenefit.headerImageFromGallery = imageName as HeaderImageFromGalleryEnum;
  };

  const imagePath = 'https://files.pxtra.de/benefit-images/';
  const loadHeaderImage = () => {
    if (newCustomBenefit.headerImageFromGallery) {
      newCustomBenefit.headerImagePreview = undefined;
      return [
        {
          name: newCustomBenefit.headerImageFromGallery,
          webkitRelativePath: imagePath + newCustomBenefit.headerImageFromGallery,
          preview: imagePath + newCustomBenefit.headerImageFromGallery,
          type: 'image/jpg'
        } as FileWithPreview
      ];
    } else if (newCustomBenefit.headerImagePreview) {
      return [newCustomBenefit.headerImagePreview];
    }
    return [];
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const setError = name.includes('name') ? setNameError : setButtonNameError;
    if (/^[a-zA-Z0-9\säöüÄÖÜß-]*$/.test(value) && value.length <= 30) {
      setError('');
      name.includes('name')
        ? (newCustomBenefit[name === 'name_de' ? 'name_de' : 'name_en'] = value)
        : (newCustomBenefit[name === 'buttonName_de' ? 'buttonLabel_de' : 'buttonLabel_en'] =
            value);
    } else {
      setError(t('custom:name_error'));
    }
  };

  const handleImageUpload = (value: unknown) => {
    if (!newCustomBenefit.headerImageFromGallery) {
      const files = value as Array<FileWithPreview>;
      if (files.length > 1) throw new Error('Maximum number of files is 1');
      if (files.length == 0) return;
      newCustomBenefit.headerImagePreview = files[0];
    }
  };

  const handleButtonLinkChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    newCustomBenefit[name === 'buttonLink_de' ? 'buttonLink_de' : 'buttonLink_en'] = value;
    if (buttonLinkError) setButtonLinkError('');
  };

  const handleButtonLinkBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    const normalized = normalizeUrl(value);
    newCustomBenefit[name === 'buttonLink_de' ? 'buttonLink_de' : 'buttonLink_en'] = normalized;
    setButtonLinkError(normalized === '' || isValidUrl(normalized) ? '' : t('custom:url_error'));
  };

  const handleTabChange = (event: ChangeEvent<object>, newValue: number) => setTabValue(newValue);

  function uploadImageComponent() {
    return (
      <>
        <FileUpload
          options={{
            maxFiles: 1,
            maxSize: 3 * 1024 * 1024,
            accept: { 'image/jpg': ['.jpg', '.png'] }
          }}
          recommendedResolution={[1280, 900]}
          label={t('custom:header_image')}
          onChange={handleImageUpload}
          onDelete={() => {
            newCustomBenefit.headerImagePreview = undefined;
            newCustomBenefit.headerImageFromGallery = undefined;
          }}
          initFiles={loadHeaderImage()}
          dropzoneWidth={'100%'}
          dropzoneHeight={'165px'}
          hideFileInfo={true}
          dataTestId="header-image-upload"
        />
        <Button variant="secondary" onClick={openGallery} className="w-full">
          <ImageIcon />

          {t('custom:choose_from_gallery')}
        </Button>
      </>
    );
  }

  return (
    <>
      <Grid container alignItems="center">
        <Grid item marginTop={-2} marginBottom={-2}>
          <ThemeProvider theme={tabTheme}>
            <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary">
              <Tab
                label={t('custom:german')}
                style={{
                  color: tabValue === 0 ? theme.palette.heaven.main : theme.palette.primary.dark,
                  fontWeight: tabValue === 0 ? 'bold' : 'normal'
                }}
              />
              {newCustomBenefit.translationAdded && (
                <Tab
                  label={t('custom:english')}
                  style={{
                    color: tabValue === 1 ? theme.palette.heaven.main : theme.palette.primary.dark,
                    fontWeight: tabValue === 1 ? 'bold' : 'normal'
                  }}
                />
              )}
            </Tabs>
          </ThemeProvider>
        </Grid>
        <Grid
          item
          marginLeft={4}
          marginTop={-2}
          marginBottom={-2}
          style={{ marginLeft: newCustomBenefit.translationAdded ? 'auto' : undefined }}
        >
          <Button variant="tertiary" onClick={handleTranslationButtonClick}>
            {newCustomBenefit.translationAdded
              ? t('custom:delete_translation')
              : t('custom:add_translation')}

            {newCustomBenefit.translationAdded && <DeleteIcon />}
          </Button>
        </Grid>
      </Grid>
      {tabValue === 0 && (
        <Grid container spacing={4} marginTop={0.5} alignItems="flex-start">
          <Grid item md={8}>
            <Grid container spacing={3} marginBottom={2}>
              <Grid item md={2}>
                <Select
                  options={iconOptions}
                  label={t('custom:icon')}
                  futureEditingLocked={true}
                  value={newCustomBenefit.icon}
                  onChange={(e) => (newCustomBenefit.icon = e.target.value as string)}
                />
              </Grid>
              <Grid item md={10}>
                <TextField
                  name={'name_de'}
                  label={t('custom:name')}
                  futureEditingLocked={true}
                  type="text"
                  placeholder={t('custom:name_hint')}
                  value={newCustomBenefit.name_de}
                  onChange={handleNameChange}
                  error={!!nameError}
                  helperText={nameError}
                />
              </Grid>
            </Grid>
            <div style={{ width: '100%' }}>
              <InputFieldLabel label={t('custom:description')} required={false} />
              <ThemeProvider theme={descriptionTheme}>
                <MuiInput
                  multiline={true}
                  minRows={7}
                  value={newCustomBenefit.description_de}
                  disableUnderline
                  onChange={(e) => (newCustomBenefit.description_de = e.target.value)}
                  placeholder={t('custom:add_text')}
                />
              </ThemeProvider>
            </div>
            <Grid item xs={12} md={12} marginTop={3}>
              <TextField
                label={t('custom:button_link')}
                type="text"
                name="buttonLink_de"
                onChange={handleButtonLinkChange}
                onBlur={handleButtonLinkBlur}
                error={!!buttonLinkError}
                helperText={buttonLinkError}
                placeholder={t('custom:button_link_hint')}
                value={newCustomBenefit.buttonLink_de}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            {uploadImageComponent()}
            <Grid item xs={12} md={12} mt={3}>
              <TextField
                name={'buttonName_de'}
                label={t('custom:button_name')}
                type="text"
                onChange={handleNameChange}
                error={!!buttonNameError}
                helperText={buttonNameError}
                placeholder={t('custom:button_name_hint')}
                value={newCustomBenefit.buttonLabel_de}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {tabValue === 1 && (
        <Grid container spacing={4} marginTop={0.5} alignItems="flex-start">
          <Grid item md={8}>
            <Grid container spacing={3} marginBottom={2}>
              <Grid item md={2}>
                <Select
                  options={iconOptions}
                  label={t('custom:icon')}
                  futureEditingLocked={true}
                  value={newCustomBenefit.icon}
                  disabled={true}
                />
              </Grid>
              <Grid item md={10}>
                <TextField
                  name={'name_en'}
                  label={t('custom:name')}
                  futureEditingLocked={true}
                  type="text"
                  placeholder={t('custom:name_hint')}
                  value={newCustomBenefit.name_en}
                  onChange={handleNameChange}
                  error={!!nameError}
                  helperText={nameError}
                  disabled={
                    newCustomBenefit.partialEditingOnly &&
                    newCustomBenefit.allowEditTranslationTitle
                  }
                />
              </Grid>
            </Grid>
            <div style={{ width: '100%' }}>
              <InputFieldLabel label={t('custom:description')} required={false} />
              <ThemeProvider theme={descriptionTheme}>
                <MuiInput
                  multiline={true}
                  minRows={7}
                  value={newCustomBenefit.description_en}
                  disableUnderline
                  onChange={(e) => (newCustomBenefit.description_en = e.target.value)}
                  placeholder={t('custom:add_text')}
                />
              </ThemeProvider>
              <Grid item xs={4} md={12} marginTop={3}>
                <TextField
                  label={t('custom:button_link')}
                  type="text"
                  name="buttonLink_en"
                  onChange={handleButtonLinkChange}
                  error={!!buttonLinkError}
                  helperText={buttonLinkError}
                  placeholder={t('custom:button_link_hint')}
                  value={newCustomBenefit.buttonLink_en}
                />
              </Grid>
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            {uploadImageComponent()}
            <Grid item xs={12} md={12} marginTop={3}>
              <TextField
                name={'buttonName_en'}
                label={t('custom:button_name')}
                type="text"
                onChange={handleNameChange}
                error={!!buttonNameError}
                helperText={buttonNameError}
                placeholder={t('custom:button_name_hint')}
                value={newCustomBenefit.buttonLabel_en}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {showGalleryDialog && (
        <GalleryDialog
          title={t('custom:choose_header_image')}
          onClose={() => setShowGalleryDialog(false)}
          open={showGalleryDialog}
          onImageSelect={handleImageSelect}
        />
      )}
      <ConfirmationDialog
        open={showDeleteTranslationDialog}
        title={t('custom:discard_translation')}
        contentText={t('custom:discard_translation_hint')}
        onConfirm={() => {
          newCustomBenefit.clear_en();
          setShowDeleteTranslationDialog(false);
          setTabValue(0);
        }}
        onClose={() => setShowDeleteTranslationDialog(false)}
        onCancel={() => setShowDeleteTranslationDialog(false)}
        confirmButtonText={t('custom:delete')}
        cancelButtonText={t('common:CANCEL')}
        hideCancelButton={false}
        variant={'delete'}
      />
      <Drawer
        label={t('custom:drawer_headline')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <>
          <Typography variant={'body2'}>{t('custom:drawer_intro')}</Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginTop: '2rem' }}>
            {t('custom:drawer_widget_headline')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans i18nKey={'custom:drawer_widget_text'} components={{ bold: <strong /> }} />
          </Typography>
          <img src={image1} alt="image1" />
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginTop: '2rem' }}>
            {t('custom:drawer_info_page_headline')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans i18nKey={'custom:drawer_info_page_text_1'} components={{ bold: <strong /> }} />
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans i18nKey={'custom:drawer_info_page_text_2'} components={{ bold: <strong /> }} />
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans i18nKey={'custom:drawer_info_page_text_3'} components={{ bold: <strong /> }} />
          </Typography>
          <img src={image2} alt="image2" />
        </>
      </Drawer>
    </>
  );
});
